import { useState } from 'react';
import type { KeuringDto } from '@/api/generated';
import { useKeuringen } from '@/api/hooks';
import { QueryBoundary } from '@/api/QueryBoundary';
import { AppFrame } from '@/design-system/chrome/AppFrame';
import { Icon } from '@/design-system/icon/Icon';
import { Btn } from '@/design-system/primitives/Btn/Btn';
import { Card } from '@/design-system/primitives/Card/Card';
import { Pill } from '@/design-system/primitives/Pill/Pill';
import { formatShortDate, toNumber } from '@/lib/format';
import {
  KeuringAttestDialog,
  KeuringCreateDialog,
  KeuringEditDialog,
  KeuringSeedDialog,
} from './KeuringDialogs';
import styles from './Keuringen.module.css';

const STATUS_LABEL: Record<KeuringDto['status'], string> = {
  Done: 'attest binnen',
  Doing: 'bezig',
  Idle: 'te plannen',
  Late: 'overschreden',
};

const STATUS_KIND: Record<KeuringDto['status'], 'ok' | 'warn' | 'idle' | 'bad'> = {
  Done: 'ok',
  Doing: 'warn',
  Idle: 'idle',
  Late: 'bad',
};

function SummCol({ n, kind, label }: { n: number; kind: string; label: string }) {
  return (
    <div>
      <div className={`row gap-6 ${styles.summRow}`}>
        <div className={styles.summNumber} style={{ color: `var(--${kind})` }}>
          {n}
        </div>
        <div className={styles.summLabel}>{label}</div>
      </div>
    </div>
  );
}

export function Keuringen() {
  const keuringen = useKeuringen();
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<KeuringDto | null>(null);
  const [attestTarget, setAttestTarget] = useState<KeuringDto | null>(null);
  const [seedOpen, setSeedOpen] = useState(false);

  return (
    <AppFrame>
      <QueryBoundary query={keuringen} loading="Keuringen laden…">
        {(page) => {
          const items = page.items;
          const done = items.filter((i) => i.status === 'Done').length;
          const doing = items.filter((i) => i.status === 'Doing').length;
          const open = items.filter((i) => i.status === 'Idle').length;
          const required = items.filter((i) => i.required).length;
          const pct = required === 0 ? 0 : Math.round(((done + doing * 0.5) / required) * 100);

          return (
            <>
              <div className={`row ${styles.headerRow}`}>
                <div>
                  <div className="eyebrow">
                    Keuringen &amp; attesten · {toNumber(page.total)} items
                  </div>
                  <h1 className={styles.title}>Keuringen-checklist</h1>
                  <p className={styles.subtitle}>
                    Alle wettelijke en contractuele controles op één plek — met termijnen,
                    verantwoordelijken en attesten.
                  </p>
                </div>
                <div className={`ml-auto row gap-8 ${styles.headerActions}`}>
                  <Btn variant="ghost" onClick={() => setSeedOpen(true)}>
                    <Icon name="clipboard" size={14} /> Standaardlijst toevoegen
                  </Btn>
                  <Btn variant="primary" onClick={() => setCreateOpen(true)}>
                    <Icon name="plus" size={14} /> Keuring inplannen
                  </Btn>
                </div>
              </div>
              <KeuringCreateDialog open={createOpen} onOpenChange={setCreateOpen} />
              <KeuringSeedDialog open={seedOpen} onOpenChange={setSeedOpen} />
              {editTarget && (
                <KeuringEditDialog
                  open={editTarget !== null}
                  onOpenChange={(o) => {
                    if (!o) setEditTarget(null);
                  }}
                  keuring={editTarget}
                />
              )}
              {attestTarget && (
                <KeuringAttestDialog
                  open={attestTarget !== null}
                  onOpenChange={(o) => {
                    if (!o) setAttestTarget(null);
                  }}
                  keuring={attestTarget}
                />
              )}

              <Card className={styles.summary}>
                <div className="row gap-24">
                  <SummCol n={done} kind="ok" label="afgerond" />
                  <SummCol n={doing} kind="warn" label="bezig" />
                  <SummCol n={open} kind="idle" label="te starten" />
                  <SummCol n={items.length - required} kind="info" label="niet verplicht" />
                  <div className={`ml-auto col ${styles.summRight}`}>
                    <div className="eyebrow">Voortgang totaal</div>
                    <div className={styles.summPct}>{pct}%</div>
                  </div>
                </div>
                <div className={styles.summBar}>
                  <span className={styles.summBarOk} />
                  <span className={styles.summBarDoing} />
                  <span className={styles.summBarIdle} />
                  <span className={styles.summBarNa} />
                </div>
              </Card>

              <Card className={styles.list}>
                {items.map((it, i) => {
                  const last = i === items.length - 1;
                  const borderColor =
                    it.status === 'Done'
                      ? 'var(--ok)'
                      : it.status === 'Doing'
                        ? 'var(--warn)'
                        : it.status === 'Idle'
                          ? 'var(--line-strong)'
                          : 'var(--bad)';
                  return (
                    <div
                      key={it.id}
                      className={`row gap-14 ${styles.item} ${last ? styles.itemLast : ''} ${
                        !it.required ? styles.itemNa : ''
                      }`}
                    >
                      <div
                        className={styles.itemDot}
                        style={{
                          borderColor,
                          background: it.status === 'Done' ? 'var(--ok)' : 'transparent',
                        }}
                      >
                        {it.status === 'Done' && (
                          <Icon name="check" size={12} color="white" strokeWidth={3} />
                        )}
                        {it.status === 'Doing' && (
                          <span
                            className={styles.itemDotInner}
                            style={{ background: borderColor }}
                          />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className={`row gap-10 ${styles.itemHead}`}>
                          <div className={styles.itemName}>{it.name}</div>
                          <span className={styles.itemReq}>
                            · {it.required ? 'wettelijk verplicht' : 'aanbevolen'}
                          </span>
                        </div>
                        <div className={`row gap-12 ${styles.itemMeta}`}>
                          <span className="row gap-4">
                            <Icon name="calendar" size={11} color="var(--ink-3)" />
                            {it.date
                              ? `uitgevoerd ${formatShortDate(it.date)}`
                              : it.dueDate
                                ? `deadline ${formatShortDate(it.dueDate)}`
                                : 'nog niet ingepland'}
                          </span>
                          <span className="row gap-4">
                            <Icon name="flag" size={11} color="var(--ink-3)" />
                            fase {it.phaseId}
                          </span>
                        </div>
                      </div>
                      <Pill kind={STATUS_KIND[it.status]} dot={false}>
                        {STATUS_LABEL[it.status]}
                      </Pill>
                      <div className="row gap-4">
                        {it.status !== 'Done' && (
                          <Btn
                            variant="ghost"
                            className={styles.rowActionBtn}
                            onClick={() => setAttestTarget(it)}
                          >
                            <Icon name="upload" size={12} /> Attest
                          </Btn>
                        )}
                        <Btn
                          variant="ghost"
                          className={styles.rowActionBtn}
                          onClick={() => setEditTarget(it)}
                          aria-label={`Bewerk ${it.name}`}
                        >
                          <Icon name="edit" size={12} />
                        </Btn>
                      </div>
                    </div>
                  );
                })}
              </Card>
            </>
          );
        }}
      </QueryBoundary>
    </AppFrame>
  );
}
